/* eslint-disable no-console */
/* eslint-disable camelcase */
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DevelopmentInfo from 'components/DevelopmentInfo';
import ImageCover from 'components/ImageCover';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useParams } from 'react-router-dom';
import InvoiceDetails from 'types/InvoiceDetails';
import CustomerInfo from 'types/CustomerInfo';
import {
  Luci_Shopping_Api_Models_OrderDetailModel,
} from '../types/api/v7.1';
import { useGetInvoice } from '../api/ShoppingApi';
import NotFound from '../components/NotFound';
import PrivacyPolicyNotification from '../components/PrivacyPolicyNotification';
import ThemeWrapper from '../components/ThemeWrapper';
import Invoice from '../types/Invoice';
import PaymentFormInfo from '../types/PaymentFormInfo';
import { DEFAULT_TAX_STRATEGY } from '../types/TaxStrategyType';
import {
  getCustomerFromStorage,
  putCustomerInStorage,
} from '../utilities/customerStorage';
import formatDateMMDDYYYY, {
  formatDateMMDDYYYYTime,
} from '../utilities/formatDate';
import { getTenantLogos } from '../utilities/logoUtilities';
import Styles from './Base.module.scss';
import InvoicePortalBilling from './InvoicePortalBilling';
import InvoicePortalConfirmation from './InvoicePortalConfirmation';
import InvoicePortalPayment from './InvoicePortalPayment';
import InvoicePortalSummary from './InvoicePortalSummary';

/**
 * PageType defines the four main steps in the invoice payment flow.
 * The flow typically follows this sequence: Summary → Billing → Payment → Confirmation
 */
type PageType = 'Summary' | 'Billing' | 'Payment' | 'Confirmation';
type Props = {
  page: PageType;
  themeMode?: 'light' | 'dark';
  onThemeModeChange?: (mode: 'light' | 'dark') => void;
  tenantIdOverride?: number;
  onTenantOverrideChange?: (id: number) => void;
};

/**
 * Base component acts as the container for the Invoice Portal application.
 * It serves as the central orchestrator that:
 * 1. Manages shared state across child pages
 * 2. Controls navigation flow and access to pages based on order status
 * 3. Fetches and initializes invoice data
 * 4. Handles loading/error states
 * 5. Provides consistent layout and branding
 *
 * Child components receive relevant props and state management functions:
 * - InvoicePortalSummary: Shows order details and initiates the checkout process
 * - InvoicePortalBilling: Collects customer billing/shipping information
 * - InvoicePortalPayment: Processes payment information
 * - InvoicePortalConfirmation: Displays receipt and order completion details
 *
 * @param page The current page in the invoice flow
 */
const Base = ({
  page,
  themeMode = 'light',
  onThemeModeChange,
  tenantIdOverride = 0,
  onTenantOverrideChange,
}: Props): JSX.Element => {
  const params = useParams() as { invoiceId: string };
  const { invoiceId } = params;
  const { invoice, isLoading, isError } = useGetInvoice(invoiceId);

  // Dev tenant override (0 = none) wins over the invoice's real tenant so the
  // DevelopmentInfo panel can preview each brand theme/logo.
  const effectiveTenantId = tenantIdOverride || invoice?.tenantId;

  const isSuspended = invoice?.isSuspended;
  const isPastDue = invoice?.invoiceDetails?.order?.isPastDue;

  // State shared across child components to maintain consistent data through the flow
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [paymentType, setPaymentType] = useState('Card');
  const [invoiceState, setInvoiceState] = useState<Invoice | undefined>(
    invoice
  );
  const [paymentFormInfo, setPaymentFormInfo] =
    useState<PaymentFormInfo | null>(null);
  const theme = useTheme();

  useEffect(() => {
    // Wait until invoice data is loaded and valid
    if (isLoading || !invoice) {
      return; // Don't proceed until data is ready
    }

    // Destructure details safely (now we know invoice is not null)
    const customer = invoice.invoiceDetails?.order?.customer;
    const shipToAddress =
      invoice.invoiceDetails?.order?.fulfillment?.shipping?.shipToAddress;
    const billingAddressFromApi = customer?.address; // Customer's primary address

    // Get locally stored customer info
    const localCustomer = getCustomerFromStorage(
      invoiceId,
      process.env.NODE_ENV || 'development'
    );

    // Construct the customer info
    const newCustomerInfo: CustomerInfo = {
      email: localCustomer?.email ?? customer?.emailAddress ?? '',
      phone: localCustomer?.phone ?? customer?.phone ?? '',

      // --- Shipping Address Logic ---
      // Priority: localCustomer -> shipToAddress -> billingAddressFromApi  (or customer name for names) -> ''
      shippingAddress: {
        firstName:
          localCustomer?.shippingAddress?.firstName ??
          customer?.firstName ??
          '',
        lastName:
          localCustomer?.shippingAddress?.lastName ?? customer?.lastName ?? '',
        line1:
          localCustomer?.shippingAddress?.line1 ??
          shipToAddress?.line1 ??
          billingAddressFromApi?.line1 ??
          '',
        line2:
          localCustomer?.shippingAddress?.line2 ??
          shipToAddress?.line2 ??
          billingAddressFromApi?.line2 ??
          '',
        city:
          localCustomer?.shippingAddress?.city ??
          shipToAddress?.city ??
          billingAddressFromApi?.city ??
          '',
        state:
          localCustomer?.shippingAddress?.state ??
          shipToAddress?.state ??
          billingAddressFromApi?.state ??
          '',
        zip:
          localCustomer?.shippingAddress?.zip ??
          shipToAddress?.zip ??
          billingAddressFromApi?.zip ??
          '',
        country:
          localCustomer?.shippingAddress?.country ??
          shipToAddress?.country ??
          billingAddressFromApi?.country ??
          'USA',
      },

      // --- Billing Address Logic --- (Remains the same)
      // Priority: localCustomer -> shippingFromAddress -> ''
      billingAddress: {
        firstName:
          localCustomer?.billingAddress?.firstName ?? customer?.firstName ?? '',
        lastName:
          localCustomer?.billingAddress?.lastName ?? customer?.lastName ?? '',
        line1:
          localCustomer?.billingAddress?.line1 ??
          shipToAddress?.line1 ??
          billingAddressFromApi?.line1 ??
          '',
        line2:
          localCustomer?.billingAddress?.line2 ??
          shipToAddress?.line2 ??
          billingAddressFromApi?.line2 ??
          '',
        city:
          localCustomer?.billingAddress?.city ??
          shipToAddress?.city ??
          billingAddressFromApi?.city ??
          '',
        state:
          localCustomer?.billingAddress?.state ??
          shipToAddress?.state ??
          billingAddressFromApi?.state ??
          '',
        zip:
          localCustomer?.billingAddress?.zip ??
          shipToAddress?.zip ??
          billingAddressFromApi?.zip ??
          '',
        country:
          localCustomer?.billingAddress?.country ??
          shipToAddress?.country ??
          billingAddressFromApi?.country ??
          'USA',
      },
    };

    // Only update state and storage if the new info is different from current state
    if (JSON.stringify(customerInfo) !== JSON.stringify(newCustomerInfo)) {
      setCustomerInfo(newCustomerInfo);
      putCustomerInStorage(
        invoiceId,
        newCustomerInfo,
        process.env.NODE_ENV || 'development'
      );
    }

    // Update invoice state separately if needed
    if (
      !invoiceState ||
      invoice.invoiceDetails?.order?.id !==
        invoiceState.invoiceDetails?.order?.id
    ) {
      setInvoiceState(invoice);
    }
  }, [invoice, isLoading, invoiceId, invoiceState, customerInfo]);

  /**
   * Callback passed to child components to update customer information.
   * Used by InvoicePortalBilling and InvoicePortalSummary to modify customer data.
   */
  const handleChange = (newCustomerInfo: CustomerInfo): void => {
    putCustomerInStorage(
      invoiceId,
      newCustomerInfo,
      process.env.NODE_ENV || 'development'
    );
    setCustomerInfo(newCustomerInfo);
  };

  if (invoice && !isLoading && !isError && customerInfo) {
    const { invoiceDetails, tenantName } = invoice;
    const { order } = invoiceDetails ?? {};
    const safeToLower = (
      val: Luci_Shopping_Api_Models_OrderDetailModel.status | undefined
    ): string | undefined => {
      return typeof val === 'string' ? val.toLowerCase() : undefined;
    };
    const orderStatus =
      safeToLower(invoiceState?.invoiceDetails?.order?.status) ||
      safeToLower(order?.status);
    const lineItems = invoiceDetails?.order?.orderItems?.length ?? 0;
    /**
     * Flow control logic that determines which pages a user can access.
     * This controls navigation and prevents users from accessing pages out of sequence
     * or when business rules don't allow it (e.g., expired invoice).
     */
    // Always allow user to visit summary page
    const allowedPages: PageType[] = [];
    const orderCancelled = orderStatus === 'cancelled';
    const orderExpired = invoiceDetails?.isExpired;

    // If order is cancelled OR expired without grace period, only allow Summary page
    if (orderCancelled || (orderExpired && !isPastDue)) {
      allowedPages.push('Summary');
      if (page !== 'Summary') {
        return <Navigate to={`/invoice/${invoiceId}`} replace />;
      }
    }

    // Allow normal flow for non-cancelled orders that are either:
    // 1. Not expired, OR
    // 2. Expired but within grace period (isPastDue = true)
    if (!orderCancelled && (!orderExpired || isPastDue)) {
      /**
       * Conditions for Summary and Billing pages:
       * - Order status must be 'open', 'invoiced', or 'finalized'
       * - Order must have line items
       */
      if (
        (orderStatus === 'open' ||
          orderStatus === 'invoiced' ||
          orderStatus === 'finalized') &&
        lineItems
      )
        allowedPages.push('Summary', 'Billing');

      /**
       * Conditions for Payment page:
       * - Order status must be 'finalized'
       * - Order must have line items
       * - Customer must have completed billing information first
       */
      if (orderStatus === 'finalized' && lineItems)
        allowedPages.push('Payment');

      /**
       * Conditions for Confirmation page:
       * - Order status must be 'completed' or 'paid' (payment successful)
       * - OR order has no line items
       */
      if (orderStatus === 'completed' || orderStatus === 'paid' || !lineItems)
        allowedPages.push('Confirmation');
    }

    // Automatic navigation rules to ensure proper flow
    // First handle confirmation page redirects
    if (allowedPages.includes('Confirmation') && page !== 'Confirmation') {
      return <Navigate to={`/invoice/${invoiceId}/confirmation`} replace />;
    }

    // For all other cases where the current page isn't in allowed pages
    // Use replace:true to ensure proper browser history behavior
    if (allowedPages.indexOf(page) < 0) {
      return <Navigate to={`/invoice/${invoiceId}/address`} replace />;
    }

    // Use the bundled per-tenant logo (with light/dark variants) rather than the
    // API's single light-only Logo, so the mark stays legible in dark mode.
    const tenantLogo = getTenantLogos(effectiveTenantId, themeMode).full;

    const pageTitles = {
      notFound: 'Invoice Not Found',
      cancelled: `${tenantName} - Invoice Cancelled - Order: ${order?.id}`,
      expired: `${tenantName} - Invoice Expired - Order: ${order?.id}`,
      pastDue: `${tenantName} - Invoice Past Due - Order: ${order?.id}`,
      summary: `${tenantName} - Invoice - Order: ${order?.id}`,
      billing: `${tenantName} - Invoice Billing - Order: ${order?.id}`,
      payment: `${tenantName} - Invoice Payment - Order: ${order?.id}`,
      confirmation: `${tenantName} - Invoice Confirmation - Order: ${order?.id}`,
    };

    let pageMetaData = pageTitles.summary;
    if (isPastDue) {
      pageMetaData = pageTitles.pastDue;
    } else if (orderExpired) {
      pageMetaData = pageTitles.expired;
    }
    if (orderCancelled) {
      pageMetaData = pageTitles.cancelled;
    }
    if (isSuspended) {
      return (
        <ThemeWrapper tenantId={effectiveTenantId} themeMode={themeMode}>
          <Helmet>
            <meta name="description" content={pageTitles.notFound} />
            <title>{pageTitles.notFound}</title>
          </Helmet>
          <DevelopmentInfo
            themeMode={themeMode}
            onThemeModeChange={onThemeModeChange}
            tenantId={effectiveTenantId}
            tenantIdOverride={tenantIdOverride}
            onTenantOverrideChange={onTenantOverrideChange}
          />
          <Grid
            container
            spacing={1}
            justifyContent="center"
            alignItems="center"
            textAlign="center"
            className="my-4"
          >
            <NotFound
              heading="Invoice Processing Disabled"
              message={
                <Typography variant="body1">
                  We are sorry but we can&apos;t currently process invoices.
                  Please contact{' '}
                  <Link
                    title={`Contact ${tenantName} Support`}
                    className={Styles.contactLink}
                    to={`mailto:${invoice?.customerContactInfo?.emailAddress}`}
                  >
                    {invoice?.customerContactInfo?.emailAddress}
                  </Link>{' '}
                  for assistance.
                </Typography>
              }
            />
          </Grid>
        </ThemeWrapper>
      );
    }

    return (
      <ThemeWrapper tenantId={effectiveTenantId} themeMode={themeMode}>
        <Helmet>
          <meta name="description" content={pageMetaData} />
          <title>{pageMetaData}</title>
        </Helmet>
        <DevelopmentInfo
          themeMode={themeMode}
          onThemeModeChange={onThemeModeChange}
          tenantId={effectiveTenantId}
          tenantIdOverride={tenantIdOverride}
          onTenantOverrideChange={onTenantOverrideChange}
        />
        {tenantLogo && (
          <Box className="my-4 text-center">
            <ImageCover
              src={tenantLogo}
              aspectRatio="auto"
              maxHeight="4rem"
              alt={`${tenantName}`}
            />
          </Box>
        )}
        {orderCancelled &&
          !invoiceDetails?.isExpired &&
          !isLoading &&
          !isError && (
            <Alert
              severity={'error'}
              sx={{ width: '100%', marginBottom: '1rem' }}
            >
              <AlertTitle>
                <strong>Order Canceled</strong>
              </AlertTitle>
              If you did not intend on canceling this order, contact the seller for assistance.
            </Alert>
          )}
        <header style={{ width: '100%' }}>
          <Grid container spacing={2} sx={{ marginBottom: '1rem' }}>
            <Grid item xs={6} justifyItems={'start'}>
              <Typography
                color={theme.palette.grey[600]}
                component="h3"
                variant="h5"
              >
                Order Number
              </Typography>
              <Typography component="h4" variant="h5">
                {order?.id}
              </Typography>
            </Grid>
            <Grid item xs={6} justifyItems={'end'}>
              <Typography
                color={theme.palette.grey[600]}
                component="h3"
                variant="h5"
              >
                Order Date
              </Typography>
              <Typography component="h4" variant="h5">
                {order?.created && formatDateMMDDYYYY(order.created.toString())}
              </Typography>
            </Grid>
            <Grid item xs={12} marginBottom={'2rem'} justifyItems={'start'}>
              <Typography
                component="h3"
                variant="h5"
                color={theme.palette.grey[600]}
              >
                Due Date
              </Typography>
              <Typography component="h4" variant="h5">
                <strong>
                  {invoiceDetails?.paymentDueDate
                    ? formatDateMMDDYYYYTime(invoiceDetails.paymentDueDate)
                    : ''}
                </strong>
                {' '}
                {isPastDue && (
                  <Typography component="span" variant="h5" style={{ textTransform: 'uppercase' }} color={theme.palette.error.main}>
                    Past Due
                  </Typography>
                )}
              </Typography>
            </Grid>
          </Grid>
        </header>
        {page !== 'Confirmation' && orderExpired && !isPastDue && (
          <Alert severity={'error'} sx={{ marginBottom: '1rem' }}>
            {' '}
            <AlertTitle>Expired</AlertTitle>This invoice was not paid before the
            payment due date. Please contact the seller for assistance.
          </Alert>
        )}

        {/*
          SUMMARY PAGE
          - Displays order item details, costs, and taxes
          - Entry point to the payment flow
          - Allows finalizing the order to proceed to billing
          - Allows finalizing the order to proceed to billing
        */}
        {allowedPages.includes('Summary') && page === 'Summary' && (
          <InvoicePortalSummary
            invoice={invoice}
            handleChange={handleChange}
            setInvoiceDetails={setInvoiceState}
            setPaymentFormInfo={setPaymentFormInfo}
            invoiceDetails={invoiceDetails as InvoiceDetails}
            invoiceId={invoiceId}
            termsOfUseUrl={invoice?.tenant?.staticDocuments?.TermsOfUse || ''}
            privacyPolicyUrl={
              invoice?.tenant?.staticDocuments?.PrivacyPolicy || ''
            }
            orderCancelled={orderCancelled}
            orderExpired={invoiceDetails?.isExpired}
            orderPastDue={isPastDue}
            taxStrategy={invoiceDetails?.taxStrategy ?? DEFAULT_TAX_STRATEGY}
          />
        )}

        {/*
          BILLING PAGE
          - Collects customer billing and shipping information
          - Validates address details
          - Allows selection of payment methods (Card/PayPal)
          - Prepares data for payment processing
        */}
        {allowedPages.includes('Billing') && page === 'Billing' && (
          <>
            <Helmet>
              <meta name="description" content={pageTitles.billing} />
              <title>{pageTitles.billing}</title>
            </Helmet>
            <InvoicePortalBilling
              invoice={invoice}
              setInvoiceDetails={setInvoiceState}
              customerInfo={customerInfo}
              handleChange={handleChange}
              invoiceId={invoiceId}
              setPaymentFormInfo={setPaymentFormInfo}
              termsOfUseUrl={invoice?.tenant?.staticDocuments?.TermsOfUse || ''}
              privacyPolicyUrl={
                invoice?.tenant?.staticDocuments?.PrivacyPolicy || ''
              }
              setPaymentType={setPaymentType}
              paymentType={paymentType}
            />
          </>
        )}

        {/*
          PAYMENT PAGE
          - Shows payment form (Credit Card form or PayPal interface)
          - Processes payment via API
          - Handles payment success/failure
          - Redirects to confirmation or displays errors
          - Requires paymentFormInfo from Billing page to be populated first
        */}
        {allowedPages.includes('Payment') &&
          page === 'Payment' &&
          (paymentFormInfo ? (
            <>
              <Helmet>
                <meta name="description" content={pageTitles.payment} />
                <title>{pageTitles.payment}</title>
              </Helmet>
              <InvoicePortalPayment
                invoice={invoice}
                customerInfo={customerInfo}
                setInvoiceDetails={setInvoiceState}
                setPaymentFormInfo={setPaymentFormInfo}
                invoiceDetails={invoiceState?.invoiceDetails}
                invoiceId={invoiceId}
                paymentFormInfo={paymentFormInfo}
                taxStrategy={
                  invoiceDetails?.taxStrategy ?? DEFAULT_TAX_STRATEGY
                }
                paymentType={paymentType}
              />
            </>
          ) : (
            // Redirect back to billing if payment form info is missing
            <Navigate
              key={`${'Payment'.toString()}redirect`}
              to={`/invoice/${invoiceId}/address`}
            />
          ))}

        {/*
          CONFIRMATION PAGE
          - Displays receipt and order completion details
          - Shows fulfillment information
          - Displays reward points (if applicable)
          - Final page in the flow, no further actions needed
        */}
        {allowedPages.includes('Confirmation') && page === 'Confirmation' && (
          <>
            <Helmet>
              <meta name="description" content={pageTitles.billing} />
              <title>{pageTitles.billing}</title>
            </Helmet>
            <InvoicePortalConfirmation invoice={invoice} />
          </>
        )}
        <PrivacyPolicyNotification
          privacyPolicyUrl={
            invoice?.tenant?.staticDocuments?.PrivacyPolicy || ''
          }
        />
      </ThemeWrapper>
    );
  }

  /**
   * Error handling for cases where:
   * 1. Invoice is not found
   * 2. API connection failed
   * 3. Loading is in progress
   */
  if (!invoice && !isLoading && !isError?.statusCode) {
    return (
      <ThemeWrapper tenantId={tenantIdOverride || undefined} themeMode={themeMode}>
        <Grid container spacing={1} justifyContent="center" alignItems="center">
          <NotFound
            heading="Order Not Found"
            message="We are sorry, this order can not be found. Please confirm the URL you were given is correct and try again."
          />
        </Grid>
      </ThemeWrapper>
    );
  }

  return (
    <ThemeWrapper tenantId={tenantIdOverride || undefined} themeMode={themeMode}>
      {!isLoading &&
        isError &&
        isError.statusCode &&
        isError.statusCode >= 400 &&
        isError.statusCode < 600 && (
          <NotFound
            heading="Can Not Contact Server"
            message="Sorry we are having issues contacting our servers. Please try
              again later. If the problem continues, please contact support."
          />
        )}
      {!isError && isLoading && (
        <div className={Styles.loading}>
          <CircularProgress data-testid="loading-spinner" />
        </div>
      )}
    </ThemeWrapper>
  );
};

export default Base;
